import * as angular from "angular";
import { AdminDataProvider } from "../../data-provider/admin.dp";
import { UserDataProvider } from "../../data-provider/user.dp";
import { User } from "../../models/user.model";
import { DialogUtils } from "../../helper/dialog.helper";
import { NavigationBarHelperService } from "../navigation/navigation-bar.helper";
import { FilterDataProvider } from "../../data-provider/filter.dp";
import { Filter } from "../../models/filter.model";
import { XeroService } from "../../services/xero.service";
import { ToastUtils } from "../../helper/toast.helper";

export interface AdminChip {
    label: string;
    /** null = "All" for the field this chip set drives */
    value: string | null;
    /** Filter field written by the chip */
    field:
        | "status"
        | "onlineOnly"
        | "accountStatus"
        | "platform"
        | "ostatus"
        | "result"
        | "trend";
    icon?: string;
    dot?: boolean;
    /** Activity lens chips: colour class for the leading dot (AV-17958). */
    dotClass?: string;
}

/**
 * Page header + quick filter row for every admin page (AV-17963). The file
 * keeps its historical name so the page templates and module wiring stay put.
 */
let AdminTabsComponent = {
    selector: "vcAdminTabs",
    templateUrl: "/angular/components/admin/admin-tabs.component.html",
    bindings: {
        activeTab: "=?",
        filter: "=?",
    },
    controller: class AdminTabsController {
        // * bindings for typescript compiler *
        public activeTab?: string;
        public filter: any;
        public navigationBarService: NavigationBarHelperService;
        // ************************************

        public chips: AdminChip[] = [];

        private readonly $scope: any;
        private $window: any;
        public __env: any;
        private localStorageService: any;
        private dataProvider: AdminDataProvider;
        private filterDataProvider: FilterDataProvider;
        private dialog: DialogUtils;
        private user: User;
        // AV-17974: region-level Xero connection state for the header actions.
        public xero: {
            loaded: boolean;
            configured: boolean;
            connected: boolean;
            status: string | null;
            tenant_name: string | null;
        } = {
            loaded: false,
            configured: false,
            connected: false,
            status: null,
            tenant_name: null,
        };
        private xeroService: XeroService;
        private toast: ToastUtils;
        private $location: any;
        private searchShortcut: ((event: any) => void) | null = null;

        constructor(
            $scope: any,
            __env: any,
            $window: any,
            localStorageService: any,
            UserDataProvider: UserDataProvider,
            AdminDataProvider: AdminDataProvider,
            FilterDataProvider: FilterDataProvider,
            NavigationBarHelperService: NavigationBarHelperService,
            DialogUtils: DialogUtils,
            $location: any,
            XeroService: XeroService,
            ToastUtils: ToastUtils
        ) {
            this.$scope = $scope;
            this.$window = $window;
            this.__env = __env;
            this.dialog = DialogUtils;
            this.$location = $location;
            this.xeroService = XeroService;
            this.toast = ToastUtils;
            this.filterDataProvider = FilterDataProvider;
            this.localStorageService = localStorageService;
            this.navigationBarService = NavigationBarHelperService;
            this.dataProvider = AdminDataProvider;
            this.user = UserDataProvider.getActiveUser();
        }

        $onInit() {
            // AV-17958: every visit opens on the Directory; the Activity lens
            // is a deliberate switch, not a remembered state.
            if (this.filter && this.hasLens()) {
                this.filter.lens = "directory";
                if (!this.filter.range) this.filter.range = 7;
                // The shared filter can still carry an Activity sort from the
                // last visit.
                this.dropSortUnsupportedBy("directory");
            } else if (this.filter) {
                delete this.filter.lens;
                delete this.filter.trend;
            }
            this.chips = AdminTabsController.chipsFor(
                this.activeTab ?? "",
                this.filter ? this.filter.lens : undefined
            );

            // Chips are single-select per page; clear any chip field this page
            // does not own so navigating between pages resets to "All".
            // Toggles (thisRegion, attached, showPresence) deliberately persist.
            if (this.filter) {
                const own = new Set(this.chips.map((c) => c.field));
                if (this.filter.lens != "activity") delete this.filter.trend;
                if (
                    !own.has("status") &&
                    this.filter.status &&
                    this.filter.status.values
                ) {
                    this.filter.status.values = [];
                }
                if (!own.has("onlineOnly")) this.filter.onlineOnly = false;
                for (const key of [
                    "accountStatus",
                    "platform",
                    "ostatus",
                    "result",
                ] as const) {
                    if (!own.has(key)) delete this.filter[key];
                }
            }

            if (this.filter && angular.isUndefined(this.filter.showPresence)) {
                this.filter.showPresence = true;
            }
            this.loadXero();
            this.announceXeroReturn();
            this.bindSearchShortcut();
        }

        $onDestroy(): void {
            const doc = this.$window && this.$window.document;
            if (this.searchShortcut && doc) {
                doc.removeEventListener("keydown", this.searchShortcut);
            }
        }

        /**
         * "/" jumps to the search box from anywhere on the page, Escape empties
         * it. Ignored while typing in a field so it never eats a slash.
         */
        private bindSearchShortcut(): void {
            // $window is stubbed in unit tests, so there may be no document.
            const doc = this.$window && this.$window.document;
            if (!doc) {
                return;
            }
            this.searchShortcut = (event: any): void => {
                const target = event.target || {};
                const tag = (target.tagName || "").toLowerCase();
                const typing =
                    tag == "input" ||
                    tag == "textarea" ||
                    tag == "select" ||
                    target.isContentEditable;
                const input = doc.getElementById("search");
                // Nothing to do without the box, or while a dialog has focus.
                if (!input || doc.querySelector("md-dialog")) {
                    return;
                }
                if (
                    event.key == "/" &&
                    !typing &&
                    !event.metaKey &&
                    !event.ctrlKey
                ) {
                    event.preventDefault();
                    input.focus();
                    return;
                }
                if (event.key == "Escape" && target === input) {
                    input.blur();
                    if (this.filter && this.filter.search) {
                        this.$scope.$applyAsync(() => {
                            this.filter.search.values = "";
                        });
                    }
                }
            };
            doc.addEventListener("keydown", this.searchShortcut);
        }

        // ---------- Xero (AV-17974) ----------

        loadXero(): void {
            this.xeroService.status().then(
                (response: object) => {
                    const json = response["data"];
                    if (json && json.success) {
                        this.xero = { loaded: true, ...json.data };
                        XeroService.shortCode = json.data.short_code || null;
                    } else {
                        this.xero.loaded = true;
                    }
                },
                () => {
                    this.xero.loaded = true;
                }
            );
        }

        /** Toast once after the OAuth round-trip, then clean the URL. */
        private announceXeroReturn(): void {
            const search = this.$location.search() || {};
            if (!search.xero) return;
            if (search.xero == "connected") {
                this.toast.success("Xero connected");
            } else {
                this.toast.error(
                    "Xero connect failed: " + (search.reason || "unknown")
                );
            }
            this.$location.search("xero", null);
            this.$location.search("reason", null);
        }

        xeroLapsed(): boolean {
            return this.xero.loaded && this.xero.status == "lapsed";
        }

        connectXero(): void {
            this.xeroService.connectUrl().then(
                (response: object) => {
                    const json = response["data"];
                    if (json && json.success && json.data && json.data.url) {
                        this.$window.location.href = json.data.url;
                    } else {
                        this.toast.error(
                            (json && json.data && json.data.message) ||
                                "Could not start the Xero connection."
                        );
                    }
                },
                (error: any) =>
                    this.toast.error(
                        (error &&
                            error.data &&
                            error.data.data &&
                            error.data.data.message) ||
                            "Could not start the Xero connection."
                    )
            );
        }

        disconnectXero($event: any): void {
            this.dialog
                .delete({
                    targetEvent: $event,
                    locals: {
                        title: "Disconnect Xero",
                        bodyText:
                            "Admin rows keep their saved Xero details, but lookups and refreshes stop until someone reconnects.",
                    },
                })
                .then(() =>
                    this.xeroService.disconnect().then(
                        () => {
                            this.toast.success("Xero disconnected");
                            this.loadXero();
                        },
                        () => this.toast.error()
                    )
                );
        }

        // ---------- header copy ----------

        eyebrow(): string {
            if (this.activeTab == "live") return "Monitoring";
            if (this.activeTab == "logins") return "Security";
            return "Directory";
        }

        title(): string {
            switch (this.activeTab) {
                case "campaigns":
                    return "Accounts";
                case "users":
                    return "Users";
                case "organisations":
                    return "Organisations";
                case "live":
                    return "Live activity";
                case "logins":
                    return "Logins";
            }
            return "";
        }

        subtitle(): string {
            if (this.isActivity()) {
                const words = this.rangeOptions().filter(
                    (o) => o.value == (this.filter.range || 7)
                )[0];
                return (
                    "Last " +
                    (words ? words.label : "7 days") +
                    " against the preceding period. Refreshed nightly at 04:00."
                );
            }
            switch (this.activeTab) {
                case "campaigns":
                    return "Every billable account across both regions.";
                case "users":
                    return "Everyone with a login, across all accounts.";
                case "organisations":
                    return "Multi-account organisations and their limits.";
                case "live":
                    return "Users active across all organisations in the last 24 hours.";
                case "logins":
                    return "All attempted and failed logins.";
            }
            return "";
        }

        // ---------- chips ----------

        /**
         * campaignstatus ids: 1 Paid, 2 Trial, 3 Cancelled, 4 Sandbox,
         * 5 Legacy (rendered as Paused). "leader" is the backend's token for
         * campaigntype = ORGANISATION.
         */
        // ---------- Activity lens (AV-17958) ----------

        hasLens(): boolean {
            return (
                this.activeTab == "campaigns" ||
                this.activeTab == "organisations"
            );
        }

        lens(): string {
            return this.filter && this.filter.lens == "activity"
                ? "activity"
                : "directory";
        }

        isActivity(): boolean {
            return this.hasLens() && this.lens() == "activity";
        }

        /**
         * Switching lens swaps the chip set and resets the chip to All. Search,
         * region and membership deliberately survive.
         */
        setLens(lens: "directory" | "activity"): void {
            if (!this.filter || !this.hasLens()) return;
            this.filter.lens = lens;
            if (!this.filter.range) this.filter.range = 7;
            // Directory chips carry across; only the trend is lens-specific.
            if (lens != "activity") delete this.filter.trend;
            this.dropSortUnsupportedBy(lens);
        }

        /**
         * A sort the lens can't do (Logins in Directory, ID in Activity)
         * would be ignored by the server, which then pages by campaign id
         * while the list pages by row count - rows go missing on scroll.
         * Drop it instead.
         */
        private dropSortUnsupportedBy(lens: "directory" | "activity"): void {
            if (
                this.filter &&
                this.filter.sort &&
                AdminTabsController.LENS_SORTS[lens].indexOf(this.filter.sort) <
                    0
            ) {
                this.filter.sort = null;
                this.filter.sortDir = "desc";
            }
        }

        /**
         * "Accounts I'm On" is one toggle for Accounts and Organisations
         * (AV-17963): set it on this page's filter and on the shared applied
         * filter, which Accounts uses and Organisations starts from.
         */
        toggleAttached(): void {
            if (!this.filter) return;
            this.filter.attached = !this.filter.attached;
            const shared =
                this.filterDataProvider &&
                this.filterDataProvider.appliedFilter;
            if (shared && shared !== this.filter) {
                shared.attached = this.filter.attached;
            }
        }

        /** Sort keys each lens's backend accepts (ADMIN_SORTS / ActivityWindow::sortsSql). */
        public static readonly LENS_SORTS: {
            [lens: string]: string[];
        } = {
            directory: ["id", "contacts", "houses", "interactions"],
            activity: [
                "interactions",
                "logins",
                "activeusers",
                "contacts",
                "last_active",
                "trend",
            ],
        };

        /** One array for the life of the controller: ng-repeat digests it. */
        public trendChipList: AdminChip[] = AdminTabsController.activityChips();

        /** The Activity lens's own chip row, shown under the Directory chips. */
        trendChips(): AdminChip[] {
            return this.isActivity() ? this.trendChipList : [];
        }

        setRange(range: number): void {
            if (!this.filter) return;
            this.filter.range = range;
        }

        /** One array for the life of the controller: ng-repeat digests it. */
        public static readonly RANGES: Array<{ value: number; label: string }> =
            [
                { value: 1, label: "24 hours" },
                { value: 7, label: "7 days" },
                { value: 30, label: "30 days" },
                { value: 90, label: "90 days" },
            ];
        public rangeOptionList = AdminTabsController.RANGES;

        rangeOptions(): Array<{ value: number; label: string }> {
            return AdminTabsController.RANGES;
        }

        /** Live count for an Activity chip, from whichever list loaded the rows. */
        /**
         * AV-18115: the number beside a chip, or null when it is unknown.
         *
         * The Activity lens keys its counts by trend; the Directory keys
         * them by the chip's own value, across status, online and platform.
         * Both carry "__total" for the All chip, which cannot be a sum
         * because the Directory chips overlap - a user can be Confirmed and
         * Online and on iOS at once.
         */
        chipCount(chip: AdminChip): number | null {
            const grid: any =
                this.dataProvider.chipCounts[(this.activeTab ?? "") + ":grid"];
            if (this.isActivity()) {
                return grid ? this.gridCount(grid, chip) : null;
            }
            const counts = this.dataProvider.chipCounts[this.activeTab ?? ""];
            if (!counts) return null;

            if (chip.value === null) {
                if (counts["__total"] !== undefined) {
                    return counts["__total"];
                }
                // Activity trends partition the rows, so summing is right.
                return Object.keys(counts)
                    .filter((k) => k !== "__total")
                    .reduce((sum, k) => sum + counts[k], 0);
            }

            // The Online chip's value is the string "true"; its count is
            // stored under the field name so it cannot collide with a
            // status value.
            const key = chip.field === "onlineOnly" ? "online" : chip.value;
            return counts[key] !== undefined ? counts[key] : null;
        }

        /**
         * Activity lens: a chip's number from the status x trend grid. A
         * status chip counts under the picked trend, a trend chip under the
         * picked status, "All" in either row lifts that row's constraint.
         */
        private gridCount(grid: any, chip: AdminChip): number {
            const statusKey =
                chip.field == "trend" ? this.pickedStatus() : chip.value;
            const trendKey =
                chip.field == "trend"
                    ? chip.value
                    : this.filter && this.filter.trend;
            let total = 0;
            Object.keys(grid).forEach((status: string): void => {
                if (
                    statusKey !== null &&
                    statusKey !== undefined &&
                    status != statusKey
                ) {
                    return;
                }
                const row = grid[status] || {};
                Object.keys(row).forEach((trend: string): void => {
                    if (trendKey && trend != trendKey) return;
                    total += Number(row[trend]) || 0;
                });
            });
            return total;
        }

        /** The Directory chip currently picked, as its grid key, or null for All. */
        private pickedStatus(): string | null {
            if (!this.filter) return null;
            if (this.filter.ostatus) return this.filter.ostatus;
            const values =
                (this.filter.status && this.filter.status.values) || [];
            return values.length == 1 ? String(values[0]) : null;
        }

        columnsKey(): string {
            return AdminDataProvider.columnsKey(
                this.activeTab ?? "",
                this.filter ? this.filter.lens : undefined
            );
        }

        static activityChips(): AdminChip[] {
            return [
                { label: "All", value: null, field: "trend" },
                {
                    label: "Dropped",
                    value: "dropped",
                    field: "trend",
                    dotClass: "ec-dot-dropped",
                },
                {
                    label: "Quiet",
                    value: "quiet",
                    field: "trend",
                    dotClass: "ec-dot-quiet",
                },
                {
                    label: "Steady",
                    value: "steady",
                    field: "trend",
                    dotClass: "ec-dot-steady",
                },
                {
                    label: "Growing",
                    value: "growing",
                    field: "trend",
                    dotClass: "ec-dot-growing",
                },
                {
                    label: "New",
                    value: "new",
                    field: "trend",
                    dotClass: "ec-dot-new",
                },
                {
                    label: "No snapshot",
                    value: "nodata",
                    field: "trend",
                    dotClass: "ec-dot-nodata",
                },
            ];
        }

        /**
         * The page's Directory chips. The lens no longer swaps them out: the
         * Activity lens keeps them and adds its trend chips as a second row
         * (trendChips), so the two can be combined.
         */
        static chipsFor(tab: string, lens?: string): AdminChip[] {
            if (tab == "campaigns") {
                return [
                    { label: "All", value: null, field: "status" },
                    { label: "Paid", value: "1", field: "status" },
                    { label: "Trial", value: "2", field: "status" },
                    { label: "Organisation", value: "leader", field: "status" },
                    { label: "Sandbox", value: "4", field: "status" },
                    { label: "Paused", value: "5", field: "status" },
                    { label: "Cancelled", value: "3", field: "status" },
                ];
            }
            if (tab == "users") {
                return [
                    { label: "All", value: null, field: "accountStatus" },
                    {
                        label: "Online",
                        value: "true",
                        field: "onlineOnly",
                        dot: true,
                    },
                    {
                        label: "Confirmed",
                        value: "Confirmed",
                        field: "accountStatus",
                    },
                    {
                        label: "Pending",
                        value: "Pending",
                        field: "accountStatus",
                    },
                    {
                        label: "iOS",
                        value: "ios",
                        field: "platform",
                        icon: "phone_iphone",
                    },
                    {
                        label: "Android",
                        value: "android",
                        field: "platform",
                        icon: "phone_android",
                    },
                    {
                        label: "Web",
                        value: "web",
                        field: "platform",
                        icon: "language",
                    },
                ];
            }
            if (tab == "organisations") {
                return [
                    { label: "All", value: null, field: "ostatus" },
                    { label: "Active", value: "Active", field: "ostatus" },
                    { label: "Paused", value: "Paused", field: "ostatus" },
                ];
            }
            if (tab == "logins") {
                return [
                    { label: "All", value: null, field: "result" },
                    { label: "Successful", value: "success", field: "result" },
                    { label: "Failed", value: "failed", field: "result" },
                ];
            }
            return [];
        }

        /** Single-select: clear every chip-driven field, then set this one. */
        selectChip(chip: AdminChip): void {
            if (!this.filter) return;
            // Each row is single-select on its own: a trend chip leaves the
            // Directory chip alone and the other way round.
            if (chip.field == "trend") {
                delete this.filter.trend;
                if (chip.value !== null) this.filter.trend = chip.value;
                return;
            }
            if (this.filter.status) this.filter.status.values = [];
            this.filter.onlineOnly = false;
            delete this.filter.accountStatus;
            delete this.filter.platform;
            delete this.filter.ostatus;
            delete this.filter.result;

            if (chip.value === null) return;
            if (chip.field == "status") {
                if (!this.filter.status) {
                    this.filter.status = { values: [], checked: true };
                }
                this.filter.status.values = [chip.value];
            } else if (chip.field == "onlineOnly") {
                this.filter.onlineOnly = true;
            } else {
                this.filter[chip.field] = chip.value;
            }
        }

        isChipActive(chip: AdminChip): boolean {
            if (!this.filter) return chip.value === null;
            if (chip.field == "trend") {
                return chip.value === null
                    ? !this.filter.trend
                    : this.filter.trend == chip.value;
            }
            const statusValues: string[] =
                (this.filter.status && this.filter.status.values) || [];
            const anySet =
                statusValues.length > 0 ||
                !!this.filter.onlineOnly ||
                !!this.filter.accountStatus ||
                !!this.filter.platform ||
                !!this.filter.ostatus ||
                !!this.filter.result;
            if (chip.value === null) return !anySet;
            if (chip.field == "status") {
                return (
                    statusValues.length == 1 && statusValues[0] == chip.value
                );
            }
            if (chip.field == "onlineOnly") return !!this.filter.onlineOnly;
            return this.filter[chip.field] == chip.value;
        }

        // ---------- toggles ----------

        /**
         * Only the accounts and organisations endpoints have a region column,
         * so the toggle would be a silent no-op on Users, Logins and Live.
         */
        /** EU only / US only: both offered on both consoles. */
        public regions = [
            { id: "eu", label: "EU" },
            { id: "usa", label: "US" },
        ];

        /** The picked region; the old boolean meant this console's own. */
        regionOnly(): string | null {
            const value = this.filter ? this.filter.thisRegion : null;
            if (value === "eu" || value === "usa") return value;
            return value === true ? this.__env.region || "eu" : null;
        }

        toggleRegionOnly(region: "eu" | "usa"): void {
            if (!this.filter) return;
            this.filter.thisRegion =
                this.regionOnly() == region ? null : region;
        }

        showRegionToggle(): boolean {
            return (
                this.activeTab == "campaigns" ||
                this.activeTab == "organisations"
            );
        }

        showAttachedToggle(): boolean {
            return (
                this.activeTab == "campaigns" ||
                this.activeTab == "organisations"
            );
        }

        showPresenceToggle(): boolean {
            return this.activeTab == "users";
        }

        showFilterPane(): boolean {
            // Users' only pane filter (Device) is covered by the platform chips.
            return this.activeTab == "campaigns";
        }

        // ---------- actions (unchanged behaviour) ----------

        openExportDialog($event: any): void {
            this.dialog.show({
                component: "vc-admin-export-dialog",
                targetEvent: $event,
                size: "large",
                locals: {
                    tab: this.activeTab,
                    filter: this.filter,
                },
            });
        }

        openColumnsDialog(columns: object[], $event: any): void {
            this.dialog
                .show({
                    component: "vc-columns-dialog",
                    targetEvent: $event,
                    clickOutsideToClose: false,
                    scope: this.$scope,
                    ariaLabel: "Choose which columns you see.",
                    class: "columns-dialog",
                    locals: {
                        columns: columns,
                        sortable: true,
                        defaults:
                            this.dataProvider.defaultColumns[this.columnsKey()],
                    },
                })
                .then((columns: object[]) => {
                    let localStorage = this.user.getLocalStorage();
                    if (!localStorage.columns) {
                        localStorage.columns = {};
                    }
                    // Column choices are kept per lens (AV-17958).
                    localStorage.columns[this.columnsKey()] = columns;
                    this.user.saveLocalStorage();
                    this.dataProvider.savePreferences({
                        columns: localStorage.columns,
                    });
                    this.dataProvider.setColumns();
                });
        }

        createOrganisation($event: any): void {
            this.dialog
                .show({
                    component: "vc-create-organisation",
                    targetEvent: $event,
                    scope: this.$scope,
                    size: "large",
                    locals: {},
                })
                .then(() => {
                    this.$window.location.reload();
                });
        }

        createCampaign($event: any): void {
            this.dialog
                .show({
                    component: "vc-admin-create-campaign",
                    targetEvent: $event,
                    scope: this.$scope,
                    size: "large",
                    locals: {},
                })
                .then(() => {
                    this.$window.location.reload();
                });
        }
    },
};

angular
    .module("admin")
    .component(AdminTabsComponent.selector, AdminTabsComponent);
